/**
 * Single source of truth for the auth tokens.
 *
 * Everything that needs a token — the fetch wrapper, the socket service, the
 * auth context — reads through here rather than touching localStorage directly,
 * so a token renewed in one place is immediately visible everywhere else. The
 * socket bug this replaces came from a token captured by value and reused
 * forever; reading through a getter makes that class of bug impossible.
 */

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function read(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    // Safari private mode and similar can throw on access.
    return null;
  }
}

function write(key: string, value: string | null) {
  if (!isBrowser()) return;
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Storage unavailable — the in-memory session still works for this tab.
  }
}

export function getAccessToken(): string | null {
  return read(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return read(REFRESH_TOKEN_KEY);
}

export function setAccessToken(token: string | null): void {
  write(ACCESS_TOKEN_KEY, token);
}

export function setTokens(
  accessToken: string,
  refreshToken?: string | null
): void {
  write(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    write(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearTokens(): void {
  write(ACCESS_TOKEN_KEY, null);
  write(REFRESH_TOKEN_KEY, null);
}

/**
 * Seconds remaining before the access token expires, read from its own `exp` claim
 * so the client never has to guess the server's configured lifetime.
 *
 * @returns Remaining seconds, or `null` if there is no readable token.
 */
export function getAccessTokenSecondsRemaining(): number | null {
  const token = getAccessToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (typeof payload?.exp !== 'number') return null;

  return payload.exp - Math.floor(Date.now() / 1000);
}

/**
 * Decodes a JWT payload without verifying it. Safe here because the value is
 * only used to decide *when* to refresh — every real authorization decision is
 * made by the server against the signature.
 */
export function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}
